/**
 * BotConfigPage — Bot type chooser.
 *
 * Previously: a generic multi-wallet bot configurator that stored private keys
 * in Azure Key Vault. That architecture was custodial (we held user keys),
 * defaulted to a single token (MCN), and only worked for the platform
 * operator. Removed entirely.
 *
 * Now: a chooser screen. The user picks the kind of bot they want; we route
 * them to that bot type's specialized creator page. Each specialized page
 * uses the user's own exchange API key (CEX bots) or their connected wallet
 * (DEX bots) — haythix never holds private keys for any user.
 */

import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Bot, Grid3X3, TrendingDown, GitBranch, Zap, Radio,
  ShieldCheck, ChevronRight, Sparkles,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { getTierConfig } from '../constants/tiers';

const BOT_TYPES = [
  {
    id: 'grid',
    name: 'Grid Bot',
    icon: Grid3X3,
    color: 'from-emerald-400 to-teal-500',
    href: '/grid-bot',
    short: 'Buy low, sell high in a range.',
    desc: 'Places a ladder of buy and sell orders within a price range you set. Captures profit on every oscillation. Best for sideways markets.',
    tier: 'gridBot',
    badge: null,
  },
  {
    id: 'dca-safety',
    name: 'DCA Safety Bot',
    icon: TrendingDown,
    color: 'from-blue-400 to-indigo-500',
    href: '/dca-safety',
    short: 'Average down on dips — with hard stops.',
    desc: 'Dollar-cost average into a position with safety nets: max drawdown, max additional buys, auto-pause on extreme moves. Different from simple DCA — it knows when to stop.',
    tier: 'gridBot',
    badge: null,
  },
  {
    id: 'conditional',
    name: 'Conditional Bot',
    icon: GitBranch,
    color: 'from-amber-400 to-orange-500',
    href: '/conditional-bots',
    short: 'IF this, THEN trade.',
    desc: 'Rule-based bots: "If BTC drops below $X, buy Y of ETH." Chain multiple conditions across pairs and indicators. Triggers fire when conditions are met.',
    tier: 'conditionalBots',
    badge: null,
  },
  {
    id: 'arbitrage',
    name: 'Arbitrage Bot',
    icon: Zap,
    color: 'from-violet-400 to-purple-500',
    href: '/arbitrage',
    short: 'Capture price gaps across exchanges.',
    desc: 'Watches the same pair across multiple connected exchanges. When the bid on one exceeds the ask on another by your threshold, fires both sides simultaneously.',
    tier: 'arbitrageDetector',
    badge: null,
  },
  {
    id: 'webhook',
    name: 'TradingView Webhook',
    icon: Radio,
    color: 'from-cyan-400 to-blue-500',
    href: '/webhooks',
    short: 'Run your TradingView alerts as bots.',
    desc: 'Connect your TradingView Pine Script alerts. When the alert fires, haythix executes the trade on your connected exchange. No coding required.',
    tier: null,
    badge: 'POPULAR',
  },
];

export default function BotConfigPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const tierCfg = getTierConfig(user?.tier);

  function hasFeature(featureKey) {
    if (!featureKey) return true; // No tier requirement
    return tierCfg?.features?.[featureKey] === true;
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/bots')} className="btn-ghost p-2">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-accent-primary flex items-center gap-2">
            <Bot className="w-6 h-6" /> Pick a bot type
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Each bot runs on your own connected exchange or wallet. haythix never holds your keys.
          </p>
        </div>
      </div>

      {/* Trust banner */}
      <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-4 flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-emerald-300">Non-custodial by design</p>
          <p className="text-xs text-emerald-300/80 mt-1">
            Bots authenticate using your exchange API key (CEX) or your connected wallet (DEX) — both already configured in Settings → Connections.
            haythix never sees or stores your private keys. Revoke any bot's access by removing the underlying API key or wallet at any time.
          </p>
        </div>
      </div>

      {/* Bot type cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {BOT_TYPES.map(type => {
          const accessible = hasFeature(type.tier);
          const Icon = type.icon;
          return (
            <Link
              key={type.id}
              to={accessible ? type.href : '/settings?tab=subscription'}
              className={`group bg-dark-800 rounded-2xl border border-dark-700/50 p-5 hover:border-dark-600 transition-all relative overflow-hidden ${accessible ? '' : 'opacity-70'}`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br ${type.color} flex-shrink-0`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="text-white font-bold">{type.name}</h3>
                    {type.badge && (
                      <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30">
                        {type.badge}
                      </span>
                    )}
                    {!accessible && (
                      <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-400 border border-violet-500/30 flex items-center gap-1">
                        <Sparkles className="w-2.5 h-2.5" /> UPGRADE
                      </span>
                    )}
                  </div>
                  <p className="text-emerald-400 text-xs font-medium">{type.short}</p>
                  <p className="text-gray-500 text-xs mt-2 leading-relaxed">{type.desc}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-white flex-shrink-0 mt-2" />
              </div>
            </Link>
          );
        })}
      </div>

      {/* Footer hint */}
      <div className="text-center text-xs text-gray-500 py-2">
        Need a custom strategy? You can also combine our <Link to="/smart-trade" className="text-cyan-400 hover:underline">Smart Trade</Link> entry/TP/SL and <Link to="/algo-orders" className="text-cyan-400 hover:underline">Algo Orders</Link> (TWAP/VWAP/Iceberg/Bracket) for any pair on your connected exchange — no bot wrapper required.
      </div>
    </div>
  );
}
